import React, {useState} from 'react';
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {getItemGroups, getItems, getPromotions} from "../../../../services/Menu/reducer";
import {RootState} from "../../../../../reducers";
import {useCart} from "../../../../services/Cart/cartService";
import {getCart} from "../../../../services/Cart/reducer";
import ItemHelper from "../../../../services/Menu/services/Item/ItemHelper";
import {parseDate} from "../../../../services/Common/Date/DateHelper";
import {ReactComponent as MenuSVG} from "../../../../../images/icons/menu.svg";
import {getStoreConfig} from "../../../../services/Store/reducer";
import useTemporarilyClosedInfo from "../../../../hooks/useTemporarilyClosedInfo";
import StoreUtils from "../../../../services/Store/StoreUtils";
import EmptyList from "../../../Common/EmptyList";
import CartItem from "./CartItem";
import Analytics from "../../../../services/Common/Analytics/Analytics";
import Pixel from "../../../../services/Common/Analytics/Pixel";
import MenuProductModal from "../../../../pages/Menu/components/Product/MenuProductModal";
import MenuEditItemStateHelper from "../../../../services/Menu/services/State/MenuEditItemStateHelper";

type Props = ReturnType<typeof mapStateToProps> & DispatchProp;

const CartItemsComponent = ({cart, items, config, promotions, itemGroups}: Props) => {
    const orderItems = cart.items;
    const {t} = useTranslation();
    const [itemToEdit, setItemToEdit] = useState<{
        item: Record<string, any>,
        itemGroup: Record<string, any> | undefined,
        state: Record<string, any>,
        orderItemId: number
    } | undefined>();
    const cartService = useCart();
    const isStorePaused = StoreUtils.isPaused(config);
    const cartPromotions = cart.promotions;
    const {handleShowTemporarilyClosedInfo, drawTemporarilyClosedInfo} = useTemporarilyClosedInfo();
    const tips = cartPromotions.filter(x => x.type == "TIP").flatMap(x => x.items).map(x => x.order_item_uid);
    const sortedOrderItems = [...cart.items].sort((a: any, b: any) => parseDate(a.created_at).getTime() - parseDate(b.created_at).getTime());

    const changeQuantity = async (
        event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLButtonElement, MouseEvent>,
        orderItem: any, value: any, prevent?: boolean
    ) => {
        event.stopPropagation();
        if (prevent) {
            if (isStorePaused) {
                handleShowTemporarilyClosedInfo();
                return;
            }
        }

        try {
            const id = orderItem.id;
            await cartService.updateItem(id, {
                ...orderItem,
                quantity: value
            });
            if (Number(value) === 0) {
                const orderItemToRemove = orderItems.find((x: any) => x.id == id);
                if (orderItemToRemove) {
                    Analytics.removeFromCart(orderItemToRemove);
                    Pixel.removeFromCart(orderItemToRemove);
                }
            }
        } catch (err) {
            console.log(err)
        }
    }

    const handleOnItemClick = (orderItem: Record<string, any>) => {
        if (isStorePaused) {
            handleShowTemporarilyClosedInfo();
            return;
        }

        const item = ItemHelper.getItem(items, orderItem.item_id);
        if (!item) return;

        const itemGroup = ItemHelper.getItemGroup(itemGroups, item.id);
        const state = MenuEditItemStateHelper.parseOrderItemToStateItem(orderItem, promotions, cart, items);
        setItemToEdit({
            item, itemGroup, state, orderItemId: orderItem.id
        });
    }

    return (
        <>{
            itemToEdit &&
            <MenuProductModal item={itemToEdit.item} config={config} show={!!itemToEdit} orderItem
                              itemGroup={itemToEdit.itemGroup} externalItemState={itemToEdit.state}
                              setShow={setItemToEdit} orderItemId={itemToEdit.orderItemId} preventGoBack/>
        }
            {drawTemporarilyClosedInfo()}
            {sortedOrderItems.length > 0 ? (
                <div className="cart-items-body">
                    {
                        sortedOrderItems.map((orderItem: any) => {
                                const item = ItemHelper.getItem(items, orderItem.item_id)
                                const promotionItem = ItemHelper.getItemPromotion(orderItem, cartPromotions);

                                return (
                                    <CartItem key={orderItem.id} item={orderItem} itemView={item}
                                              handleChangeQuantity={changeQuantity} tips={tips}
                                              onClick={handleOnItemClick} promotion={promotionItem}
                                    />
                                )
                            }
                        )
                    }
                </div>
            ) : (
                <EmptyList icon={<MenuSVG/>} title={t("constraints.internal.no_items_in_cart")}
                           className="cart-no-items-body"/>
            )}
        </>
    )
}

const mapStateToProps = (state: RootState) => ({
    items: getItems(state.menu),
    cart: getCart(state.cart),
    config: getStoreConfig(state.config),
    itemGroups: getItemGroups(state.menu),
    promotions: getPromotions(state.menu),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartItemsComponent));